import React, { FC, useState } from "react";
import { CheckIcon, DocumentDuplicateIcon } from "@heroicons/react/20/solid";

interface NodeUuidCopyProps {
  nodeId?: string;
  stepId?: string | null;
  className?: string;
}

const shortenUuid = (value: string) =>
  value.length > 18 ? `${value.slice(0, 8)}...${value.slice(-6)}` : value;

const copyToClipboard = async (value: string) => {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(value);
    return;
  }

  const input = document.createElement("textarea");
  input.value = value;
  input.setAttribute("readonly", "true");
  input.style.position = "fixed";
  input.style.opacity = "0";
  document.body.appendChild(input);
  input.select();
  document.execCommand("copy");
  document.body.removeChild(input);
};

const NodeUuidCopy: FC<NodeUuidCopyProps> = ({ nodeId, stepId, className }) => {
  const [copied, setCopied] = useState(false);
  const value = stepId || nodeId;
  if (!value) return null;

  const label = stepId ? "Step UUID" : "Node UUID";

  const onCopy = async () => {
    await copyToClipboard(value);
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1200);
  };

  return (
    <button
      type="button"
      onClick={onCopy}
      className={`inline-flex min-w-0 items-center gap-1 rounded border border-themed-border bg-themed-background px-2 py-1 text-[11px] font-medium leading-none text-themed-textSecondary hover:border-indigo-300 hover:text-themed-text ${
        className ?? ""
      }`}
      title={`Copy ${label}: ${value}`}
      aria-label={`Copy ${label}`}
    >
      <span className="max-w-[150px] truncate font-mono">
        {shortenUuid(value)}
      </span>
      {copied ? (
        <CheckIcon className="h-3.5 w-3.5 text-green-500" />
      ) : (
        <DocumentDuplicateIcon className="h-3.5 w-3.5" />
      )}
    </button>
  );
};

export default NodeUuidCopy;
